/* eslint-disable no-void */
/* eslint-disable prefer-destructuring */
import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { read, utils } from 'xlsx';
import FileUploadFormDisplay from './FileUploadForm';
import FileUploadContext, { FileUploadCtx } from './FileUploadContext';
import FileUploadDialogue from './FileUploadDialogue';
import { FileUploadDialogueHandlers, DialogueHandlerProps, FileUploadForm } from './types';
import { useClientLogin } from '../../../providers/ClientLogin.provider';
import FileUploadReview from './FileUploadReview/FileUploadReview';
import BulkTransactionDetails from './BulkTransactionDetails/BulkTransactionDetails';
import usePostIQ11Tokenize from '../../../hooks/useApi/usePostIQ11Tokenize';
import usePostAddInteracEmail from '../../../hooks/useApi/usePostAddInteracEmail';
import { useClientData } from '@/providers/ClientData.provider';

type FC = FunctionComponent;

const UploadFilesContainer: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'Contacts.FileUpload.Container' });
  const {
    response: { Country },
  } = useClientData();
  const [fileUploadForm, setFileUploadForm] = useState<FileUploadForm>({
    Service: 'Debit',
    Product: Country === 'US' ? 'ACH' : 'EFT',
    Currency: Country == 'US' ? 'USD' : 'CAD',
    FileName: '',
    FileBase64: '',
  });

  const [fileUploadError, setFileUploadError] = useState('');
  const [showFileUploadAlert, setShowFileUploadAlert] = useState(false);
  const [showInvalidSizeAlert, setShowInvalidSizeAlert] = useState(false);
  const [showInvalidFileAlert, setShowInvalidFileAlert] = useState(false);
  const [locationState, setLocationState] = useState<'upload' | 'review' | 'confirm'>('upload');
  const [, , tokenizeAsync] = usePostIQ11Tokenize({});
  const [, , addInteracEmailAsync] = usePostAddInteracEmail({});
  const [transactionData, setTransactionData] = useState<
    {
      [key: string]: string | number | undefined | null;
    }[]
  >([]);
  const [proccesedBulkFile, setProccesedBulkFile] = useState<
    {
      [key: string]: string | number | undefined | null;
    }[]
  >([]);
  const { response } = useClientLogin();
  const CanadianProvinces = vp.CANADIAN_PROVENCES;
  const USProvinces = vp.US_STATES;
  const countries = vp.COUNTRIES;
  const { Box } = vp;
  const fileUploadCreation: FileUploadCtx = {
    showFileUploadAlert,
    showInvalidSizeAlert,
    showInvalidFileAlert,
    fileUploadError,
  };

  const ProcessBulkFile = (upload: FileUploadForm): void => {
    const ext = upload.FileName.split('.').pop()?.toLowerCase();

    if (ext === 'csv') {
      const wb = read(upload.FileBase64, { type: 'base64', raw: true });
      const lines: string[][] = [];
      wb.SheetNames.forEach((sheetName) => {
        const csv = utils.sheet_to_csv(wb.Sheets[sheetName], { FS: ';', blankrows: false });
        if (csv.length) {
          const csvArr = csv.split('\n');
          lines.push(csvArr);
        }
      });

      const results = lines[0];

      setProccesedBulkFile(parseCSV(results, upload));

      setLocationState('review');
    } else {
      setShowInvalidFileAlert(true);
    }
  };

  useEffect(() => {
    if (proccesedBulkFile.length > 0) {
      setTransactionData(proccesedBulkFile);
    }
  }, [proccesedBulkFile]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<FileUploadDialogueHandlers>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: FileUploadDialogueHandlers = {
        upload: {
          fail: {
            default: () => {
              setShowFileUploadAlert(false);
              setFileUploadError('');
            },
          },
          invalid: {
            default: () => {
              setShowInvalidFileAlert(false);
              setFileUploadError('');
            },
          },
          size: {
            default: () => {
              setShowInvalidSizeAlert(false);
              setFileUploadError('');
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onUpload: (values: FileUploadForm) => {
      const upload = { ...values };
      setFileUploadForm(upload);
      ProcessBulkFile(upload);
    },
    onSubmit: () => {
      void createBeneficiary(transactionData);
    },
  };

  return (
    <FileUploadContext.Provider value={fileUploadCreation}>
      <Box flx="col">
        <Box flx="col">
          {locationState === 'upload' && <FileUploadFormDisplay defaultValues={fileUploadForm} handlers={handlers} />}

          {locationState === 'review' && (
            <FileUploadReview
              product={fileUploadForm.Product}
              fileName={fileUploadForm.FileName}
              numberOfTransactions={transactionData.length}
              transactionData={transactionData}
              submit={handlers.onSubmit}
              backToPrev={() => {
                setLocationState('upload');
              }}
            />
          )}
          {locationState === 'confirm' && (
            <BulkTransactionDetails
              transactionData={transactionData}
              backToPrev={() => {
                setFileUploadForm({
                  Service: 'Debit',
                  Product: fileUploadForm.Product,
                  Currency: 'USD',
                  FileName: '',
                  FileBase64: '',
                });

                setLocationState('upload');
              }}
            />
          )}
        </Box>
      </Box>
      <FileUploadDialogue handleDialogue={handlers.handleDialogue} />
    </FileUploadContext.Provider>
  );

  function parseCSV(
    fileData: string[],
    upload: FileUploadForm,
  ): {
    [key: string]: string | number | undefined;
  }[] {
    if (upload.Product === 'e-Transfer') {
      return processCSVEtransfer(fileData);
    }
    if (upload.Product === 'EFT') {
      return processCSVEFT(fileData);
    }
    if (upload.Product === 'ACH') {
      return processCSVACH(fileData);
    }
    return [];
  }

  function processCSVEtransfer(fileData: string[]) {
    const beneficiaries = [];
    for (let index = 0; index < fileData.length; index += 1) {
      const values = fileData[index].split(';');

      if (values[0].trim() !== 'Currency' && values.length > 1) {
        const beneficiary: {
          [key: string]: string | number | undefined;
        } = {};
        beneficiary.RecipientName = `${values[1]}`.trim();
        beneficiary.Email = `${values[2]}`.trim();
        beneficiary.SecurityQuestion = `${values[3]}`.trim();
        beneficiary.SecurityResponse = `${values[4]}`.trim();
        beneficiary.ClientReferenceNumber = `${values[6]}`.trim();
        beneficiary.Currency = `${values[0]}`.trim();
        beneficiary.Memo = `${values[5]}`.trim();
        beneficiary.Type = 'Interac E-Transfer';
        beneficiary.Country = 'CA';
        beneficiaries.push(beneficiary);
      }
    }
    return beneficiaries;
  }
  function processCSVEFT(fileData: string[]) {
    const beneficiaries = [];
    for (let index = 0; index < fileData.length; index += 1) {
      const values = fileData[index].split(';');
      if (values[0].trim() !== 'Currency' && values.length > 1) {
        const beneficiary: {
          [key: string]: string | number | undefined;
        } = {};
        beneficiary.CompanyName = parseName(`${values[3]}`.trim());
        beneficiary.FirstName = parseName(`${values[1]}`.trim());
        beneficiary.LastName = parseName(`${values[2]}`.trim());
        beneficiary.AccountHolderName = beneficiary.CompanyName
          ? beneficiary.CompanyName
          : `${beneficiary.FirstName} ${beneficiary.LastName}`;
        beneficiary.Currency = `${values[0]}`.trim();
        beneficiary.Address1 = `${values[4]}`.trim();
        beneficiary.City = `${values[5]}`.trim();
        beneficiary.Province = parseProvince(`${values[6]}`.trim());
        beneficiary.Country = parseCountry(`${values[7]}`.trim());
        beneficiary.PostalCode = `${values[8]}`.trim();
        beneficiary.ClientReferenceNumber = `${values[9]}`.trim();
        beneficiary.FinancialInstitutionNumber = parseNumber(completeWithZeros(['l', `${values[10]}`.trim(), 3]));
        beneficiary.BranchTransitNumber = parseNumber(values[11]);
        beneficiary.AccountNumber = parseNumber(values[12]);
        beneficiary.Type = 'Bank Account';
        const bank = vp.getInstitution(parseInt(values[10], 10));
        if (bank[0]) {
          beneficiary.InstitutionName = bank[0].name;
        } else {
          beneficiary.InstitutionName = '';
        }
        beneficiaries.push(beneficiary);
      }
    }
    return beneficiaries;
  }
  function processCSVACH(fileData: string[]) {
    const beneficiaries = [];
    for (let index = 0; index < fileData.length; index += 1) {
      const values = fileData[index].split(';');
      if (values[0].trim() !== 'Currency' && values.length > 1) {
        const beneficiary: {
          [key: string]: string | number | undefined;
        } = {};
        beneficiary.CompanyName = parseName(`${values[3]}`.trim());
        beneficiary.FirstName = parseName(`${values[1]}`.trim());
        beneficiary.LastName = parseName(`${values[2]}`.trim());
        beneficiary.AccountHolderName = beneficiary.CompanyName
          ? beneficiary.CompanyName
          : `${beneficiary.FirstName} ${beneficiary.LastName}`;
        beneficiary.Currency = `${values[0]}`.trim();
        beneficiary.Address1 = `${values[4]}`.trim();
        beneficiary.City = `${values[5]}`.trim();
        beneficiary.Province = parseProvince(`${values[6]}`.trim(), 'US');
        beneficiary.Country = parseCountry(`${values[7]}`.trim());
        beneficiary.PostalCode = `${values[8]}`.trim();
        beneficiary.ClientReferenceNumber = `${values[9]}`.trim();
        beneficiary.ABARoutingNumber = parseNumber(completeWithZeros(['l', `${values[10]}`.trim(), 3]));
        beneficiary.AccountNumber = parseNumber(values[11]);
        beneficiary.Type = 'Bank Account';
        beneficiary.InstitutionName = '';
        beneficiary.PhoneNumber = `${values[12]}`.trim();
        beneficiaries.push(beneficiary);
      }
    }
    return beneficiaries;
  }
  function getKeyByValue(object: { [key: string]: string }, value: string): string {
    return Object.keys(object).find((key) => object[key].toLowerCase() === value.toLowerCase()) || '';
  }
  function getIsoCountry(name: string) {
    const ctr = Object.values(countries)
      .filter((f) => f.Country.toUpperCase() === name.toUpperCase())
      .map((c) => c.Abbreviation);
    return ctr[0];
  }
  function parseCountry(value: string): string {
    if (value) {
      if (value.length > 2) {
        if (value.toLowerCase() === 'canada') {
          return 'CA';
        }
        if (getIsoCountry(value)) {
          return getIsoCountry(value);
        }

        return value.toUpperCase();
      }
      return value.toUpperCase();
    }
    return value.toUpperCase();
  }
  function parseNumber(value: string): string {
    if (value) {
      return value.replace(/[\D]+/g, '');
    }
    return value;
  }
  function parseName(value: string): string {
    if (value) {
      return value.replace(/[^a-zA-Z0-9=$\s.*-]+/g, '');
    }

    return value;
  }
  function parseProvince(value: string, country = 'CA'): string | undefined {
    let isDefined = false;
    if (value) {
      if (value.length > 2) {
        if (country === 'CA') {
          return getKeyByValue(CanadianProvinces, value);
        }
        if (country === 'US') {
          return getKeyByValue(USProvinces, value);
        }
        return undefined;
      }
      if (value.toUpperCase() === 'PQ') {
        return 'QC';
      }
      if (value.toUpperCase() === 'QB') {
        return 'QC';
      }
      if (value.toUpperCase() === 'NF') {
        return 'NL';
      }
      if (country === 'CA') {
        isDefined = Object.prototype.hasOwnProperty.call(CanadianProvinces, value.toUpperCase());
      }
      if (country === 'US') {
        isDefined = Object.prototype.hasOwnProperty.call(USProvinces, value.toUpperCase());
      }

      if (isDefined) {
        return value.toUpperCase();
      }
      return undefined;
    }
    return value.toUpperCase();
  }

  async function createBeneficiary(
    listing: {
      [key: string]: string | number | undefined | null;
    }[],
  ) {
    const errors2: {
      [key: string]: string | number | undefined | null;
    }[] = [];
    const promises = [];
    for (let index = 0; index < listing.length; index += 1) {
      const beneficiary = listing[index];
      if (fileUploadForm.Product === 'EFT' || fileUploadForm.Product === 'Electronic Fund Transfer') {
        if (
          beneficiary.Address1 === 'undifined' ||
          beneficiary.Address1 === 'undefined undefined' ||
          beneficiary.City === 'undifined' ||
          beneficiary.Province === 'undifined' ||
          beneficiary.PostalCode === 'undifined' ||
          beneficiary.Country === 'undifined'
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidAddress', { defaultValue: 'Invalid Parameter(s): Address' });
          errors2.push(beneficiary);
        } else if (
          (beneficiary.CompanyName == null || beneficiary.CompanyName === '') &&
          (beneficiary.FirstName == null || beneficiary.FirstName === '') &&
          (beneficiary.LastName == null || beneficiary.LastName === '')
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidCompanyName', { defaultValue: 'Invalid Parameter(s): Company Name' });
          errors2.push(beneficiary);
        } else if (
          (beneficiary.FirstName == null || beneficiary.FirstName === '') &&
          (beneficiary.CompanyName == null || beneficiary.CompanyName === '')
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidFirstName', { defaultValue: 'Invalid Parameter(s): First Name' });
          errors2.push(beneficiary);
        } else if (
          (beneficiary.LastName == null || beneficiary.LastName === '') &&
          (beneficiary.CompanyName == null || beneficiary.CompanyName === '')
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidLastName', { defaultValue: 'Invalid Parameter(s): Last Name' });
          errors2.push(beneficiary);
        } else if (
          !validateEmail(beneficiary.Email?.toString() || '') &&
          beneficiary.Email != null &&
          beneficiary.Email !== ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidEmail', { defaultValue: 'Invalid Parameter(s): Email' });
          errors2.push(beneficiary);
        } else if (
          !validatePhoneNumber(beneficiary.PhoneNumber?.toString() || '') &&
          beneficiary.PhoneNumber != null &&
          beneficiary.PhoneNumber !== ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidPhoneNumber', { defaultValue: 'Invalid Parameter(s): Phone Number' });
          errors2.push(beneficiary);
        } else if (
          !validateAccount(beneficiary.AccountNumber?.toString() || '') ||
          beneficiary.AccountNumber == null ||
          beneficiary.AccountNumber === ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidAccountNumber', { defaultValue: 'Invalid Parameter(s): Account Number' });
          beneficiary.AccountNumber = null;
          errors2.push(beneficiary);
        } else if (beneficiary.PostalCode == null || beneficiary.PostalCode === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidPostalCode', { defaultValue: 'Invalid Parameter(s): Postal Code' });
          beneficiary.PostalCode = null;
          errors2.push(beneficiary);
        } else if (beneficiary.Country == null || beneficiary.Country === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidCountry', { defaultValue: 'Invalid Parameter(s): Country' });
          beneficiary.Country = null;
          errors2.push(beneficiary);
        } else if (beneficiary.Province == null || beneficiary.Province === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidProvince', { defaultValue: 'Invalid Parameter(s): Province' });
          beneficiary.Province = null;
          errors2.push(beneficiary);
        } else if (beneficiary.City == null || beneficiary.City === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidCity', { defaultValue: 'Invalid Parameter(s): City' });
          beneficiary.City = null;
          errors2.push(beneficiary);
        } else if (beneficiary.Address1 == null || beneficiary.Address1 === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidAddress', { defaultValue: 'Invalid Parameter(s): Address' });
          beneficiary.Address1 = null;
          errors2.push(beneficiary);
        } else if (
          !validateTransit(beneficiary.BranchTransitNumber?.toString() || '') ||
          beneficiary.BranchTransitNumber == null ||
          beneficiary.BranchTransitNumber === ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidBranchTransitNumber', { defaultValue: 'Invalid Parameter(s): Branch Transit Number' });
          beneficiary.BranchTransitNumber = null;
          errors2.push(beneficiary);
        } else if (
          !validateInstitution(beneficiary.FinancialInstitutionNumber?.toString() || '') ||
          beneficiary.FinancialInstitutionNumber == null ||
          beneficiary.FinancialInstitutionNumber === '' ||
          beneficiary.InstitutionName == null ||
          beneficiary.InstitutionName === ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidFinancialInstitutionNumber', { defaultValue: 'Invalid Parameter(s): Financial Institution Number' });
          beneficiary.FinancialInstitutionNumber = null;
          errors2.push(beneficiary);
        } else if (
          beneficiary.Currency == null ||
          beneficiary.Currency === '' ||
          (beneficiary.Currency !== 'CAD' && beneficiary.Currency !== 'USD')
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidCurrency', { defaultValue: 'Invalid Parameter(s): Currency' });
          beneficiary.FinancialInstitutionNumber = null;
          errors2.push(beneficiary);
        } else {
          promises.push(
            eft(
              {
                PortalUser: response.LoginUserName,
                AccountHolderName:
                  beneficiary.CompanyName !== null && beneficiary.CompanyName !== ''
                    ? String(beneficiary.CompanyName)
                    : `${String(beneficiary.FirstName) || ''} ${String(beneficiary.LastName) || ''}`,
                AccountNumber: String(beneficiary.AccountNumber),
                FinancialInstitutionNumber: String(beneficiary.FinancialInstitutionNumber),
                BranchTransitNumber: String(beneficiary.BranchTransitNumber),
                ClientReferenceNumber: String(beneficiary.ClientReferenceNumber),
                ClientControlled: false,
              },
              listing,
              index,
            ),
          );
        }
      } else if (fileUploadForm.Product === 'ACH' || fileUploadForm.Product === 'Electronic Fund Transfer') {
        if (
          beneficiary.Address1 === 'undifined' ||
          beneficiary.Address1 === 'undefined undefined' ||
          beneficiary.City === 'undifined' ||
          beneficiary.Province === 'undifined' ||
          beneficiary.PostalCode === 'undifined' ||
          beneficiary.Country === 'undifined'
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidAddress', { defaultValue: 'Invalid Parameter(s): Address' });
          errors2.push(beneficiary);
        } else if (
          (beneficiary.CompanyName == null || beneficiary.CompanyName === '') &&
          (beneficiary.FirstName == null || beneficiary.FirstName === '') &&
          (beneficiary.LastName == null || beneficiary.LastName === '')
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidCompanyName', { defaultValue: 'Invalid Parameter(s): Company Name' });
          errors2.push(beneficiary);
        } else if (
          (beneficiary.FirstName == null || beneficiary.FirstName === '') &&
          (beneficiary.CompanyName == null || beneficiary.CompanyName === '')
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidFirstName', { defaultValue: 'Invalid Parameter(s): First Name' });
          errors2.push(beneficiary);
        } else if (
          (beneficiary.LastName == null || beneficiary.LastName === '') &&
          (beneficiary.CompanyName == null || beneficiary.CompanyName === '')
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidLastName', { defaultValue: 'Invalid Parameter(s): Last Name' });
          errors2.push(beneficiary);
        } else if (
          !validateEmail(beneficiary.Email?.toString() || '') &&
          beneficiary.Email != null &&
          beneficiary.Email !== ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidEmail', { defaultValue: 'Invalid Parameter(s): Email' });
          errors2.push(beneficiary);
        } else if (
          !validatePhoneNumber(beneficiary.PhoneNumber?.toString() || '') &&
          beneficiary.PhoneNumber != null &&
          beneficiary.PhoneNumber !== ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidPhoneNumber', { defaultValue: 'Invalid Parameter(s): Phone Number' });
          errors2.push(beneficiary);
        } else if (
          !validateAccount(beneficiary.AccountNumber?.toString() || '') ||
          beneficiary.AccountNumber == null ||
          beneficiary.AccountNumber === ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidAccountNumber', { defaultValue: 'Invalid Parameter(s): Account Number' });
          beneficiary.AccountNumber = null;
          errors2.push(beneficiary);
        } else if (beneficiary.PostalCode == null || beneficiary.PostalCode === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidPostalCode', { defaultValue: 'Invalid Parameter(s): Postal Code' });
          beneficiary.PostalCode = null;
          errors2.push(beneficiary);
        } else if (beneficiary.Country == null || beneficiary.Country === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidCountry', { defaultValue: 'Invalid Parameter(s): Country' });
          beneficiary.Country = null;
          errors2.push(beneficiary);
        } else if (beneficiary.Province == null || beneficiary.Province === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidProvince', { defaultValue: 'Invalid Parameter(s): Province' });
          beneficiary.Province = null;
          errors2.push(beneficiary);
        } else if (beneficiary.City == null || beneficiary.City === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidCity', { defaultValue: 'Invalid Parameter(s): City' });
          beneficiary.City = null;
          errors2.push(beneficiary);
        } else if (beneficiary.Address1 == null || beneficiary.Address1 === '') {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidAddress', { defaultValue: 'Invalid Parameter(s): Address' });
          beneficiary.Address1 = null;
          errors2.push(beneficiary);
        } else if (
          !validateABARoutingNumber(beneficiary.ABARoutingNumber?.toString() || '') ||
          beneficiary.ABARoutingNumber == null ||
          beneficiary.ABARoutingNumber === ''
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidABARoutingNumber', { defaultValue: 'Invalid Parameter(s): ABA Routing Number' });
          beneficiary.ABARoutingNumber = null;
          errors2.push(beneficiary);
        } else if (
          beneficiary.Currency == null ||
          beneficiary.Currency === '' ||
          (beneficiary.Currency !== 'CAD' && beneficiary.Currency !== 'USD')
        ) {
          beneficiary.Id = index + 1;
          beneficiary.Message = t('invalidCurrency', { defaultValue: 'Invalid Parameter(s): Currency' });
          beneficiary.FinancialInstitutionNumber = null;
          errors2.push(beneficiary);
        } else {
          promises.push(
            eft(
              {
                PortalUser: response.LoginUserName,
                AccountHolderName:
                  beneficiary.CompanyName !== null && beneficiary.CompanyName !== ''
                    ? String(beneficiary.CompanyName)
                    : `${String(beneficiary.FirstName) || ''} ${String(beneficiary.LastName) || ''}`,
                AccountNumber: String(beneficiary.AccountNumber),
                ABARoutingNumber: String(beneficiary.ABARoutingNumber),
                ClientReferenceNumber: String(beneficiary.ClientReferenceNumber),
                ClientControlled: false,
                Address1: String(beneficiary.Address1) || '',
                City: String(beneficiary.City) || '',
                Province: String(beneficiary.Province) || '',
                PostalCode: String(beneficiary.PostalCode) || '',
                PhoneNumber: String(beneficiary.PhoneNumber) || '',
                Country: String(beneficiary.Country) || '',
              },
              listing,
              index,
            ),
          );
        }
      } else if (
        validateIsOnlyNumbers(beneficiary.RecipientName?.toString() || '') ||
        beneficiary.RecipientName == null ||
        beneficiary.RecipientName === ''
      ) {
        beneficiary.Id = index + 1;
        beneficiary.Message = t('invalidRecipientName', { defaultValue: 'Invalid Parameter(s): RecipientName' });
        errors2.push(beneficiary);
      } else if (beneficiary.Currency == null || beneficiary.Currency === '' || beneficiary.Currency !== 'CAD') {
        beneficiary.Id = index + 1;
        beneficiary.Message = t('invalidCurrency', { defaultValue: 'Invalid Parameter(s): Currency' });
        beneficiary.FinancialInstitutionNumber = null;
        errors2.push(beneficiary);
      } else if (
        !validateEmail(beneficiary.Email?.toString() || '') ||
        beneficiary.RecipientName == null ||
        beneficiary.RecipientName === ''
      ) {
        beneficiary.Id = index + 1;
        beneficiary.Message = t('invalidEmail', { defaultValue: 'Invalid Parameter(s): Email' });
        errors2.push(beneficiary);
      } else if (
        !validatePhoneNumber(beneficiary.PhoneNumber?.toString() || '') &&
        beneficiary.PhoneNumber != null &&
        beneficiary.PhoneNumber !== ''
      ) {
        beneficiary.Id = index + 1;
        beneficiary.Message = t('invalidPhoneNumber', { defaultValue: 'Invalid Parameter(s): Phone Number' });
        errors2.push(beneficiary);
      } else {
        promises.push(
          emt(
            {
              PortalUser: response.LoginUserName,
              RecipientName: String(beneficiary.RecipientName || ''),
              RecipientEmailAddress: String(beneficiary.Email || ''),
              Question: String(beneficiary.SecurityQuestion || ''),
              Answer: String(beneficiary.SecurityResponse || ''),
              ClientControlled: false,
            },
            listing,
            index,
          ),
        );
      }
    }
    const promisResults = await Promise.all(promises);
    setProccesedBulkFile([...errors2, ...promisResults].sort((a, b) => Number(a.Id) - Number(b.Id)));
    setLocationState('confirm');
  }
  async function emt(
    beneficiary: vp.InteracAddEmailRequest,
    listing: {
      [key: string]: string | number | undefined | null;
    }[],
    index: number,
  ): Promise<{
    [key: string]: string | number | undefined | null;
  }> {
    const beneficiaryObject = listing[index];
    const addInteracresponse = await addInteracEmailAsync(beneficiary);
    beneficiaryObject.Id = index + 1;
    if (addInteracresponse.Success) {
      beneficiaryObject.Message = t('successfullySubmitted', { defaultValue: 'Successfully submitted' });
    } else {
      beneficiaryObject.Message = addInteracresponse.ErrorMessage;
    }
    return beneficiaryObject;
  }
  async function eft(
    beneficiary: vp.IQ11TokenizeRequest,
    listing: {
      [key: string]: string | number | undefined | null;
    }[],
    index: number,
  ): Promise<{
    [key: string]: string | number | undefined | null;
  }> {
    const beneficiaryObject = listing[index];
    const tokenizeResponse = await tokenizeAsync(beneficiary);

    beneficiaryObject.Id = index + 1;
    if (tokenizeResponse.Success) {
      beneficiaryObject.Message = t('successfullySubmitted', { defaultValue: 'Successfully submitted' });
    } else {
      beneficiaryObject.Message = tokenizeResponse.ErrorMessage;
    }
    return beneficiaryObject;
  }
  function validateEmail(email: string): boolean {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }
  function validateInstitution(institution: string): boolean {
    const re = /^\d{1,3}$/;
    return re.test(String(institution).toLowerCase());
  }
  function validateTransit(transit: string): boolean {
    const re = /^\d{1,5}$/;
    return re.test(String(transit).toLowerCase());
  }
  function validateAccount(account: string): boolean {
    const re = /^\d{6,12}$/;
    return re.test(String(account).toLowerCase());
  }
  function validateIsOnlyNumbers(text: string): boolean {
    const re = /^\d+$/;
    return re.test(String(text).toLowerCase());
  }
  function validatePhoneNumber(text: string): boolean {
    const re = /^[a-zA-Z0-9()-+.\s]*$/;
    return re.test(String(text).toLowerCase());
  }
  function validateABARoutingNumber(ABARoutingNumber: string): boolean {
    const re = /^0\d{8}$/;
    return re.test(String(ABARoutingNumber).toLowerCase());
  }
  function completeWithZeros(params: [string, string, number]) {
    if (!params[0]) {
      return '';
    }

    if (!params[1]) {
      return '';
    }

    if (!params[2]) {
      return '';
    }

    let z = '';
    const qty = params[2] - params[1].length;
    for (let i = 0; i < qty; i += 1) {
      z += '0';
    }
    return params[0] === 'l' ? z + params[1] : params[1] + z;
  }
};

export default UploadFilesContainer;
